import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import produtosUsadosOSService from 'features/estoque/services/produtosUsadosOSService';
import materialBaseInfraService from 'features/infraestrutura/services/materialBaseInfraService';
import estoqueMinimosService from 'features/infraestrutura/services/estoqueMinimosService';
import { indexarMinimos } from 'features/infraestrutura/utils/estoqueMinimo';
import ProdutosAlmoxarifadosFiltradosTable from './Components/ProdutosAlmoxarifadosFiltradosTable';
import ClearCacheButton from 'features/estoque/components/ClearCacheButton';
import PeriodoDisplay from '../PeriodoDisplay';

const ESTOQUE_GERAL = 'geral';
const ESTOQUE_INFRA = 'infra';

const estruturaVazia = { produtos_almoxarifados_filtrados: [] };

const extrairArrayProdutosAlmoxarifados = (valor) => {
  let atual = valor;

  for (let depth = 0; depth < 4; depth += 1) {
    if (Array.isArray(atual)) {
      return atual;
    }

    if (!atual || typeof atual !== 'object') {
      return [];
    }

    atual = atual.produtos_almoxarifados_filtrados;
  }

  return Array.isArray(atual) ? atual : [];
};

const extrairProdutosGeral = (response) => {
  if (response?.data) {
    if (response.success && response.data) {
      return extrairArrayProdutosAlmoxarifados(response.data.data ?? response.data);
    }

    return extrairArrayProdutosAlmoxarifados(response.data?.produtos_almoxarifados_filtrados);
  }

  return extrairArrayProdutosAlmoxarifados(response?.produtos_almoxarifados_filtrados);
};

const extrairProdutosInfra = (response) => {
  if (Array.isArray(response?.data?.produtos_filtrados)) {
    return response.data.produtos_filtrados;
  }

  if (Array.isArray(response?.produtos_filtrados)) {
    return response.produtos_filtrados;
  }

  return [];
};

const ProdutosAlmoxarifadosFiltrados = () => {
  const [dadosCompilados, setDadosCompilados] = useState(estruturaVazia);
  const [minimos, setMinimos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tipoEstoque, setTipoEstoque] = useState(ESTOQUE_GERAL);
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [range] = useState({ presetId: 'mtd', start: toISO(startOfMonth), end: toISO(today) });

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (tipoEstoque === ESTOQUE_INFRA) {
        // O mínimo é leitura aqui: quem edita é o painel de Infraestrutura, para não existirem
        // dois lugares de escrita para a mesma configuração. Falhar a busca dos mínimos não pode
        // derrubar a tela de saldo, então ela cai para lista vazia sozinha.
        const [response, minimosCarregados] = await Promise.all([
          materialBaseInfraService.buscarProdutosFiltrados(),
          estoqueMinimosService.buscarMinimos().catch(() => []),
        ]);
        setDadosCompilados({ produtos_almoxarifados_filtrados: extrairProdutosInfra(response) });
        setMinimos(minimosCarregados);
      } else {
        const response = await produtosUsadosOSService.buscarProdutosAlmoxarifadosFiltrados({
          data_inicio: range.start,
          data_fim: range.end,
        });
        setDadosCompilados({ produtos_almoxarifados_filtrados: extrairProdutosGeral(response) });
        setMinimos([]);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError(error.message || 'Erro ao carregar dados');
      setDadosCompilados(estruturaVazia);
      setMinimos([]);
    } finally {
      setLoading(false);
    }
  }, [range.end, range.start, tipoEstoque]);

  // Carregar dados ao montar o componente
  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    const handleCompanyChange = () => {
      setError(null);
      carregarDados();
    };

    window.addEventListener('company-changed', handleCompanyChange);
    window.addEventListener('company-context-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
      window.removeEventListener('company-context-changed', handleCompanyChange);
    };
  }, [carregarDados]);

  const produtos = dadosCompilados.produtos_almoxarifados_filtrados || [];
  const isEstoqueInfra = tipoEstoque === ESTOQUE_INFRA;
  const indiceMinimos = useMemo(() => indexarMinimos(minimos), [minimos]);

  return (
    <div className="space-y-6">
      <div className="bg-[color:var(--surface)] rounded-xl border border-[color:var(--border)] shadow-[0_8px_28px_rgba(15,23,42,0.08)] p-4 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
            Tipo de Estoque
          </h3>
          <div className="flex items-center justify-center sm:justify-end space-x-4">
            <span className={`text-sm font-medium ${tipoEstoque === ESTOQUE_GERAL ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-muted)]'}`}>
              Geral
            </span>
            <button
              type="button"
              onClick={() => {
                setTipoEstoque(tipoEstoque === ESTOQUE_GERAL ? ESTOQUE_INFRA : ESTOQUE_GERAL);
                setError(null);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 ${
                isEstoqueInfra
                  ? 'bg-[color:var(--accent)] border-transparent'
                  : 'bg-[color:var(--surface-muted)] border-[color:var(--border)]'
              }`}
              aria-pressed={isEstoqueInfra}
              aria-label="Alternar tipo de estoque"
            >
              <span
                className={`${
                  isEstoqueInfra ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-1 ring-black/10 transition-transform`}
              />
            </button>
            <span className={`text-sm font-medium ${isEstoqueInfra ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-muted)]'}`}>
              Infra
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {!isEstoqueInfra ? (
          <div className="flex items-center gap-3">
            <h3 className="text-base font-semibold text-[color:var(--text-primary)]">Período</h3>
            <PeriodoDisplay start={range.start} end={range.end} />
          </div>
        ) : (
          <div />
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          {isEstoqueInfra ? (
            <button
              type="button"
              onClick={carregarDados}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-md border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2 text-sm font-medium text-[color:var(--text-primary)] shadow-sm hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Recarregando...' : 'Recarregar Dados'}
            </button>
          ) : (
            <ClearCacheButton
              endpoint="produtos-almoxarifados-filtrados"
              params={{
                data_inicio: range.start,
                data_fim: range.end
              }}
              onReload={carregarDados}
            />
          )}
        </div>
      </div>

      {error && (
        <div className="bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-[color:var(--status-bad)]" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-[color:var(--text-primary)]">
                Erro ao carregar dados
              </h3>
              <div className="mt-2 text-sm text-[color:var(--text-muted)]">
                <p>{error}</p>
                <p className="mt-1">
                  <button
                    type="button"
                    onClick={carregarDados}
                    className="font-medium underline text-[color:var(--status-bad)] hover:opacity-80"
                  >
                    Tentar novamente
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* O Geral vem do n8n sem id_almox, então não há como casar a configuração: fica como era. */}
      <ProdutosAlmoxarifadosFiltradosTable
        data={produtos}
        loading={loading}
        minimos={isEstoqueInfra ? indiceMinimos : null}
      />
    </div>
  );
};

export default ProdutosAlmoxarifadosFiltrados;
